'use client'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import React, { useEffect, useState } from 'react'
import { OnPageNavItem } from '@/utils/onPageNav/buildOnPageNavStructure'

interface OnPageNavigationProps {
  title?: string
  onPageNavItems: OnPageNavItem[]
  className?: string
}

export const OnPageNavigation: React.FC<OnPageNavigationProps> = ({
  title,
  onPageNavItems,
  className,
}) => {
  const [activeSection, setActiveSection] = useState('')
  const t = useTranslations('onPageNavigation')

  useEffect(() => {
    const handleScroll = () => {
      let currentSection = ''

      const checkSection = (items: OnPageNavItem[]) => {
        items.forEach((item) => {
          const element = document.getElementById(item.id)
          if (element) {
            const scrollPosition = window.scrollY + 300
            const elementTop = element.offsetTop
            const elementBottom = elementTop + element.offsetHeight

            if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
              currentSection = item.id
            }
          }
          if (item.children) checkSection(item.children)
        })
      }

      checkSection(onPageNavItems)
      setActiveSection(currentSection)
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => window.removeEventListener('scroll', handleScroll)
  }, [onPageNavItems])

  return (
    <div className={`w-full font-body ${className}`}>
      <p className="mb-2 text-[#737373] text-label-XS-semibold">{t('title')}</p>
      <ul>
        {onPageNavItems.map((item) => (
          <OnPageNavItemComponent key={item.id} item={item} activeSection={activeSection} />
        ))}
      </ul>
    </div>
  )
}

const OnPageNavItemComponent: React.FC<{
  item: OnPageNavItem
  activeSection: string
  title?: string
}> = ({ item, activeSection }) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const element = document.getElementById(item.id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      // Update URL without triggering a scroll
      window.history.pushState({}, '', item.link)
    }
  }

  return (
    <React.Fragment>
      <li>
        <Link
          href={item.link}
          onClick={handleClick}
          className={`block py-1.5 transition-all ${
            activeSection === item.id
              ? 'text-text-base text-label-XS-semibold'
              : 'text-[#737373] text-label-XS-regular'
          } canhover:hover:translate-x-0.5 duration-150 ease-in-out`}
        >
          {item.label}
        </Link>
        {item.children && (
          <ul className={`ml-3`}>
            {item.children.map((child) => (
              <OnPageNavItemComponent key={child.id} item={child} activeSection={activeSection} />
            ))}
          </ul>
        )}
      </li>
    </React.Fragment>
  )
}
